import { dropRightWhile, isEmptyObject, union } from 'es-toolkit'
import { pickBy } from 'es-toolkit/compat'
import { shallow } from 'zustand/shallow'

import {
  applyResponsePartDelta,
  isClosedResponseMessage,
  isModelRequestPart,
  isModelResponsePart,
} from '@/components/chat-v2/lib/messageDelta'
import {
  findConfirmedOptimisticUserMessageId,
  findDuplicateOptimisticUserMessageIds,
  hasUserPromptContent,
} from '@/components/chat-v2/lib/optimisticMessages'
import { queuedMessageId } from '@/components/chat-v2/lib/queuedSend'
import {
  applySubagentEventToSnapshot,
  resolveResyncSubagentSnapshot,
  type SubagentSnapshotsByToolCallId,
} from '@/components/chat-v2/lib/subagentEvents'

import {
  AbstractChatSession,
  ChatCompactionStatus,
  ChatMessage,
  ChatRunState,
  ChatSubagentEvent,
  ModelRequestPart,
  ModelResponsePart,
  MsgDelta,
  MsgNew,
  NewUserContent,
  SubagentChatSnapshot,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'

/**
 * Socket-owned live-session model.
 *
 * The streaming hot path mutates plain-JS drafts here and pings topic
 * subscribers; React reads versioned immutable snapshots through
 * `useSyncExternalStore`. Data is PULL-based — any code can synchronously read
 * current state — so notifications may coalesce but reads are never stale, and
 * no flush fences or cross-store seeding protocols exist.
 *
 * Cost model per `msg:delta`: O(streaming message tail). Messages hydrated
 * from the server keep their object identity untouched (zero copies); only a
 * message that streams gets a parts draft (one O(parts) copy on first
 * mutation), and only its snapshot rematerializes — O(its parts) — when a
 * subscriber actually renders. Thread arrays rematerialize lazily per version,
 * reusing per-message snapshots, so unchanged messages keep identity across
 * the whole stream.
 *
 * Divergence from the legacy session-shaped pipeline: `init_*` deltas whose
 * `index_in_thread` skips ahead used to leave `undefined` holes in
 * `current_thread` that every consumer defensively skipped. The model keeps
 * hole positions internally (so later inits still land at their index) but
 * drops them from materialized arrays.
 *
 * Content vs structure: a delta that grows an EXISTING part (text/thinking
 * content, tool-call args) is content — it pings only its part topic and
 * `thread:any`. Everything else (new message, new part, part replacement,
 * deferred-tool buckets, hydration, branch switches) is structural.
 */

export type ChatStreamTopic =
  | 'runState'
  | 'session'
  | 'queue'
  | 'sent'
  | 'thread:structure'
  | 'thread:any'
  | `part:${string}`
  | `subagent:${string}`

/** Run-lifecycle fields; parity with the legacy ChatSocketStateStore shape. */
export interface ChatRunSnapshot {
  isStreaming: boolean
  runState: ChatRunState
  compactionStatus: ChatCompactionStatus
  activeRunId: UUID | null
  /** react-use-websocket ReadyState; -1 = uninstantiated. */
  socketReadyState: number
}

/** Server-declared session fields the runtime view renders from. */
export interface SessionMetaSnapshot {
  streamState: 'idle' | 'streaming' | 'finishing'
  currentRunId: UUID | null
  followups: string[] | undefined
  modelName: string | null
}

export interface ThreadShapeSnapshot {
  /** Bumped on structural changes only. */
  structureVersion: number
  /** Bumped on every change, content included. */
  contentVersion: number
  currentThreadIds: readonly string[]
}

/** A message sent while a reply streams; it shows here until a run takes it in. */
export interface QueuedChatMessage {
  queueId: string
  content: string | NewUserContent[]
}

export type HydratableChatSession = AbstractChatSession & {
  current_followups?: string[]
  stream_state?: SessionMetaSnapshot['streamState']
  subagent_snapshots_by_tool_call_id?: Record<string, SubagentChatSnapshot>
}

interface MessageEntry {
  /** Immutable as of `snapshotVersion`; === the hydrated object until first mutation. */
  snapshot: ChatMessage
  snapshotVersion: number
  version: number
  /** Mutable parts draft; created lazily on first mutation. */
  draftParts: (ModelRequestPart | ModelResponsePart)[] | null
  draftDeferredToolCalls: ChatMessage['deferred_tool_calls'] | undefined
  draftHasDeferredToolCalls: boolean
}

export interface ChatStreamModel {
  subscribe(topic: ChatStreamTopic, callback: () => void): () => void

  // ---- hydration --------------------------------------------------------
  /**
   * Replaces live state from a server session payload.
   *
   * `source: 'socket'` (channel:start / resync results) is authoritative: it
   * replaces messages AND subagent snapshots and re-seeds delta sequencing
   * from each message's `last_delta_sequence`. `source: 'fetch'` (query
   * fetch/refetch success) replaces messages EXCEPT those with live-applied
   * deltas since the last boundary (closed fetched copies still supersede)
   * and only merges MISSING subagent snapshots — live appends may be newer
   * than the fetched copy.
   */
  hydrateSession(
    session: HydratableChatSession,
    source: 'socket' | 'fetch',
  ): void
  getSessionId(): UUID | null

  // ---- stream writes ----------------------------------------------------
  /** Sequence-buffered; out-of-order deltas wait for their predecessors. */
  applyMessageDelta(event: MsgDelta): void
  applySubagentEvent(event: ChatSubagentEvent): void
  /** Replaces the current-thread projection (branch switch, optimistic sends). */
  setCurrentThread(messages: readonly ChatMessage[]): void
  /**
   * Next authoritative thread slot (holes included). Local appends MUST take
   * their index from here: the materialized thread drops holes, so its
   * length undercounts whenever the server holds messages this client never
   * received (e.g. a cancel-mid-tool-call finalization).
   */
  getNextThreadIndex(): number
  /** Message occupying an authoritative thread slot, if any. */
  getMessageAtThreadIndex(index: number): ChatMessage | undefined
  /**
   * Places a locally-created message (optimistic send, awaiting-indexing
   * placeholder) at its `index_in_thread` slot, padding holes as needed.
   * Unlike `setCurrentThread`, this preserves thread-slot alignment.
   */
  placeLocalMessage(message: ChatMessage): void
  /**
   * Applies an authoritative msg:new: places the message at its index,
   * replaces the optimistic user message it confirms, and drops optimistic
   * duplicates of the same user content.
   */
  applyNewMessage(event: MsgNew<ChatMessage>): void
  hasDeltaGap(): boolean
  clearDeltaState(): void
  setSessionRunStatus(
    streamState: SessionMetaSnapshot['streamState'],
    currentRunId?: UUID | null,
  ): void
  setFollowups(followups: string[] | undefined): void

  // ---- queued sends -----------------------------------------------------
  enqueueMessage(message: QueuedChatMessage): void
  /** Drops queued messages: a run took them in, or a Stop dropped them all. */
  dequeueMessages(queueIds?: readonly string[]): void
  getQueuedMessages(): readonly QueuedChatMessage[]

  // ---- the viewer's sends -----------------------------------------------
  /** Counts a message the viewer sent, into the running reply or not. */
  noteSent(): void
  getSentCount(): number

  // ---- run lifecycle (ChatSocketStateStore parity) ----------------------
  setRunStatus(runState: ChatRunState, activeRunId?: UUID | null): void
  setRunState(runState: ChatRunState): void
  setIsStreaming(isStreaming: boolean): void
  setActiveRunId(activeRunId: UUID | null): void
  setCompactionStatus(status: ChatCompactionStatus): void
  setSocketReadyState(readyState: number): void

  // ---- reads (versioned; identity-stable between changes) ---------------
  getRunSnapshot(): ChatRunSnapshot
  getIsStreaming(): boolean
  getRunState(): ChatRunState
  getSessionMeta(): SessionMetaSnapshot
  getThreadShape(): ThreadShapeSnapshot
  getMessage(messageId: string): ChatMessage | undefined
  getCurrentThread(): readonly ChatMessage[]
  getAllMessages(): readonly ChatMessage[]
  /** Live text (or streaming args string) of one part; bypasses snapshots. */
  getPartText(messageId: string, partIndex: number): string | undefined
  /** Each message deltas are streaming into, as applied so far. */
  getStreamedMessages(): ChatMessage[]
  getSubagentSnapshot(
    toolCallId: string,
  ): Readonly<SubagentChatSnapshot> | undefined
  getSubagentSnapshots(): SubagentSnapshotsByToolCallId | undefined
}

const warnUnknownMessage = (event: MsgDelta): void => {
  console.warn('[chat-stream-model] dropping delta for unknown message', {
    messageId: event.message_id,
    sequence: event.sequence,
    operation: event.delta.op,
  })
}

class ChatStreamModelImpl implements ChatStreamModel {
  constructor(
    private readonly scheduleSubagentPingFlush?: SubagentPingFlushScheduler,
  ) {}

  /**
   * Topic hub. Hand-rolled instead of `mitt` on purpose: topic keys are
   * unbounded (`part:<messageId>:<partIndex>` per streamed part), so
   * unsubscribe MUST delete emptied topics — mitt keeps an empty handler
   * array per key forever, which leaks across a long session.
   */
  private readonly subscribers = new Map<ChatStreamTopic, Set<() => void>>()

  private sessionId: UUID | null = null

  private run: ChatRunSnapshot = {
    isStreaming: false,
    runState: 'idle',
    compactionStatus: 'idle',
    activeRunId: null,
    socketReadyState: -1,
  }

  private sessionMeta: SessionMetaSnapshot = {
    streamState: 'idle',
    currentRunId: null,
    followups: undefined,
    modelName: null,
  }

  /**
   * Message store + orderings. Invariants:
   * - `allMessageIds` is append-ordered and duplicate-free; `allMessageIdSet`
   *   mirrors it for O(1) membership. Mutate them only together.
   * - `currentThreadIds` is indexed by the server's `msg_index` slots and is
   *   sparse: init deltas may skip indexes; holes drop at materialization.
   */
  private readonly messagesById = new Map<string, MessageEntry>()
  private currentThreadIds: (string | undefined)[] = []
  private allMessageIds: string[] = []
  private readonly allMessageIdSet = new Set<string>()

  /**
   * Version counters and the caches they guard. Only `invalidateThread` may
   * touch these: the counters are what make `getThreadShape` /
   * `getCurrentThread` / `getAllMessages` identity-stable between changes,
   * which `useSyncExternalStore` consumers rely on to bail out of renders.
   */
  private structureVersion = 0
  private contentVersion = 0
  private threadShapeCache: ThreadShapeSnapshot | null = null
  private currentThreadCache: { version: number; value: ChatMessage[] } | null =
    null
  private allMessagesCache: { version: number; value: ChatMessage[] } | null =
    null

  private subagentSnapshots: SubagentSnapshotsByToolCallId | undefined

  /** Replaced, never mutated, so each change is a new snapshot. */
  private queuedMessages: readonly QueuedChatMessage[] = []
  private sentCount = 0

  /**
   * Delta sequence gate. `gateDelta` is the only writer: it buffers
   * out-of-order events per message and drains them in sequence, so a delta
   * NEVER reaches `applyDelta` ahead of its predecessors.
   */
  private readonly nextExpectedSequence = new Map<string, number>()
  private readonly deltaBuffers = new Map<string, Map<number, MsgDelta>>()

  // ---- subscriptions ----------------------------------------------------

  subscribe(topic: ChatStreamTopic, callback: () => void): () => void {
    let callbacks = this.subscribers.get(topic)
    if (!callbacks) {
      callbacks = new Set()
      this.subscribers.set(topic, callbacks)
    }
    callbacks.add(callback)
    return () => {
      callbacks.delete(callback)
      if (callbacks.size === 0) this.subscribers.delete(topic)
    }
  }

  private ping(topic: ChatStreamTopic): void {
    const callbacks = this.subscribers.get(topic)
    if (!callbacks) return
    // Copy: a callback may (un)subscribe while we iterate.
    for (const callback of [...callbacks]) callback()
  }

  private pingStructural(): void {
    this.ping('thread:structure')
    this.ping('thread:any')
  }

  // ---- hydration --------------------------------------------------------

  hydrateSession(
    session: HydratableChatSession,
    source: 'socket' | 'fetch',
  ): void {
    const sameSessionFetch = source === 'fetch' && this.sessionId === session.id

    // Terminal response state is monotonic: cancel/done fire several
    // concurrent refetches, and a snapshot read BEFORE finalization
    // persisted can resolve AFTER one read post-finalization. Never let
    // such a stale copy reopen a response this client already saw closed —
    // that flickered the Stopped notice and could drop it entirely until
    // the next refetch. Socket hydrations (resync results) stay
    // authoritative and replace wholesale.
    const closedResponses: ReadonlyMap<string, ChatMessage> = sameSessionFetch
      ? new Map(
          [...this.messagesById.values()]
            .map((entry) => this.materializeSnapshot(entry))
            .filter(isClosedResponseMessage)
            .map((message) => [message.id, message]),
        )
      : new Map()
    const preferClosed = (message: ChatMessage): ChatMessage => {
      const closed = closedResponses.get(message.id)
      if (closed === undefined || isClosedResponseMessage(message)) {
        return message
      }
      return closed
    }

    // Streaming twin of the closed-state rule: a mid-stream fetch was
    // snapshotted before deltas already applied here, and the untouched
    // sequence gate would never re-send the difference. The gate keys are
    // exactly the messages with live-applied state since the last boundary
    // (seeded on socket hydration, cleared at done/fail/cancel) — keep
    // their entries. A closed fetched copy is newer and still replaces.
    const liveEntries = new Map<string, MessageEntry>()
    if (sameSessionFetch) {
      for (const id of this.nextExpectedSequence.keys()) {
        const entry = this.messagesById.get(id)
        if (entry) liveEntries.set(id, entry)
      }
    }

    // A resync's overlay copy is a point-in-time read, and live deltas published
    // while it was in flight run ahead of it. Where the gate already passed the copy's sequence, or the copy
    // predates the message, the live entry is the exact newer state: keep it
    // and its gate, as `replaceSubagentSnapshots` does for subagents. Held
    // deltas replay against the new gates instead of being dropped.
    const sameSessionSocket =
      source === 'socket' && this.sessionId === session.id
    const liveGates = new Map(
      sameSessionSocket ? this.nextExpectedSequence : [],
    )
    const aheadEntries = new Map<string, MessageEntry>()
    for (const id of liveGates.keys()) {
      const entry = this.messagesById.get(id)
      if (entry) aheadEntries.set(id, entry)
    }
    const heldDeltas = sameSessionSocket
      ? [...this.deltaBuffers.values()].flatMap((buffer) => [
          ...buffer.values(),
        ])
      : []
    const keptLive = new Set<string>()

    this.sessionId = session.id
    this.messagesById.clear()
    this.allMessageIds = []
    this.allMessageIdSet.clear()
    for (const message of [
      ...session.all_messages,
      ...session.current_thread,
    ]) {
      if (this.messagesById.has(message.id)) continue
      const live = liveEntries.get(message.id)
      const ahead = aheadEntries.get(message.id)
      if (live !== undefined && !isClosedResponseMessage(message)) {
        this.messagesById.set(message.id, live)
      } else if (
        ahead !== undefined &&
        !isClosedResponseMessage(message) &&
        (liveGates.get(message.id) ?? 0) >
          (message.last_delta_sequence ?? -1) + 1
      ) {
        this.messagesById.set(message.id, ahead)
        keptLive.add(message.id)
      } else {
        this.insertMessage(preferClosed(message))
      }
      this.appendAllMessageId(message.id)
    }
    this.currentThreadIds = session.current_thread.map((message) => message.id)

    // Streamed messages the copy predates entirely: restore them at their
    // authoritative slots so their remaining deltas still find them. A socket
    // copy predates a message only while the run it reports is still going.
    const predated = [
      ...liveEntries,
      ...((session.stream_state ?? 'idle') === 'idle' ? [] : aheadEntries),
    ]
    for (const [id, entry] of predated) {
      if (this.messagesById.has(id)) continue
      this.messagesById.set(id, entry)
      this.appendAllMessageId(id)
      if (sameSessionSocket) keptLive.add(id)
      const index = entry.snapshot.index_in_thread
      this.padThreadTo(index)
      this.currentThreadIds[index] = id
    }

    this.setSessionMeta({
      streamState: session.stream_state ?? 'idle',
      currentRunId: session.current_run_id,
      followups: session.current_followups,
      modelName: session.current_model_name ?? session.initial_model_name,
    })

    const fetchedSnapshots = session.subagent_snapshots_by_tool_call_id
    if (source === 'socket') {
      this.nextExpectedSequence.clear()
      this.deltaBuffers.clear()
      for (const message of session.all_messages) {
        if (message.last_delta_sequence != null) {
          this.nextExpectedSequence.set(
            message.id,
            message.last_delta_sequence + 1,
          )
        }
      }
      for (const id of keptLive) {
        this.nextExpectedSequence.set(id, liveGates.get(id) ?? 0)
      }
      for (const event of heldDeltas) this.gateDelta(event)
      this.replaceSubagentSnapshots(fetchedSnapshots)
    } else if (fetchedSnapshots) {
      this.mergeMissingSubagentSnapshots(fetchedSnapshots)
    }

    // The events that drop a waiting message's chip are missed while the
    // socket is away. The copy names every message a run took in, and only
    // those go: the rest may still be held behind a reply waiting on its
    // user, and their runs drop them when they start.
    this.dequeueMessages(
      session.all_messages.flatMap(
        (message) => queuedMessageId(message.message) ?? [],
      ),
    )

    this.invalidateThread(true)
    this.pingStructural()
  }

  /**
   * Authoritative replacement, with one guard per stream:
   * `resolveResyncSubagentSnapshot` keeps whatever live text the fetched
   * copy of the same running stream has not reached yet (taking it would
   * rewind visible Result text). Pings every id whose snapshot changed.
   */
  private replaceSubagentSnapshots(
    fetchedSnapshots: SubagentSnapshotsByToolCallId | undefined,
  ): void {
    const previous = this.subagentSnapshots
    if (fetchedSnapshots === previous) return
    const toolCallIds = union(
      Object.keys(previous ?? {}),
      Object.keys(fetchedSnapshots ?? {}),
    )
    const next = pickBy(
      Object.fromEntries(
        toolCallIds.map((toolCallId) => [
          toolCallId,
          resolveResyncSubagentSnapshot(
            previous?.[toolCallId],
            fetchedSnapshots?.[toolCallId],
          ),
        ]),
      ),
      (snapshot): snapshot is Readonly<SubagentChatSnapshot> =>
        snapshot !== undefined,
    )
    this.subagentSnapshots =
      isEmptyObject(next) && fetchedSnapshots === undefined ? undefined : next
    toolCallIds
      .filter((toolCallId) => previous?.[toolCallId] !== next[toolCallId])
      .forEach((toolCallId) => this.ping(`subagent:${toolCallId}`))
  }

  /** Merges only missing ids — live appends may be newer than the fetch. */
  private mergeMissingSubagentSnapshots(
    fetchedSnapshots: Record<string, SubagentChatSnapshot>,
  ): void {
    const current = this.subagentSnapshots
    const added = Object.entries(fetchedSnapshots).filter(
      ([toolCallId]) =>
        current === undefined ||
        !Object.prototype.hasOwnProperty.call(current, toolCallId),
    )
    if (added.length === 0) return
    this.subagentSnapshots = {
      ...(current ?? {}),
      ...Object.fromEntries(added),
    }
    added.forEach(([toolCallId]) => this.ping(`subagent:${toolCallId}`))
  }

  getSessionId(): UUID | null {
    return this.sessionId
  }

  // ---- stream writes ----------------------------------------------------

  applyMessageDelta(event: MsgDelta): void {
    this.gateDelta(event)
  }

  private gateDelta(event: MsgDelta): void {
    const known = this.nextExpectedSequence.get(event.message_id)
    if (known !== undefined && event.sequence < known) return

    let buffer = this.deltaBuffers.get(event.message_id)
    if (!buffer) {
      buffer = new Map()
      this.deltaBuffers.set(event.message_id, buffer)
    }
    buffer.set(event.sequence, event)

    let nextExpected = known ?? 0
    for (const sequence of [...buffer.keys()]) {
      if (sequence < nextExpected) buffer.delete(sequence)
    }
    while (buffer.has(nextExpected)) {
      const buffered = buffer.get(nextExpected)
      if (!buffered) break
      buffer.delete(nextExpected)
      this.applyDelta(buffered)
      nextExpected += 1
    }
    this.nextExpectedSequence.set(event.message_id, nextExpected)
    if (buffer.size === 0) this.deltaBuffers.delete(event.message_id)
  }

  applySubagentEvent(event: ChatSubagentEvent): void {
    const toolCallId = event.parent_tool_call_id
    const current = this.subagentSnapshots?.[toolCallId]
    const next = applySubagentEventToSnapshot(current, event.subagent_event)
    if (next === current && this.subagentSnapshots) return
    this.subagentSnapshots = {
      ...(this.subagentSnapshots ?? {}),
      [toolCallId]: next,
    }
    this.pingSubagent(toolCallId)
  }

  /**
   * Subagent deltas are the socket's firehose and arrive one WebSocket
   * message per macrotask, which React cannot batch: without coalescing
   * every delta is its own card render. With a flush scheduler the snapshot
   * still updates synchronously (reads never lag) and only the
   * notification defers, collapsing all deltas between flushes into one
   * render per touched card.
   */
  private readonly pendingSubagentPings = new Set<string>()
  private isSubagentFlushScheduled = false

  private pingSubagent(toolCallId: string): void {
    if (!this.scheduleSubagentPingFlush) {
      this.ping(`subagent:${toolCallId}`)
      return
    }
    this.pendingSubagentPings.add(toolCallId)
    if (this.isSubagentFlushScheduled) return
    this.isSubagentFlushScheduled = true
    this.scheduleSubagentPingFlush(() => {
      this.isSubagentFlushScheduled = false
      const flushed = [...this.pendingSubagentPings]
      this.pendingSubagentPings.clear()
      flushed.forEach((id) => this.ping(`subagent:${id}`))
    })
  }

  setCurrentThread(messages: readonly ChatMessage[]): void {
    for (const message of messages) {
      const existing = this.messagesById.get(message.id)
      if (!existing || existing.snapshot !== message) {
        this.insertMessage(message)
      }
      this.appendAllMessageId(message.id)
    }
    // Callers pass materialized (hole-dropped) threads; a positional rebuild
    // would compact the server msg_index alignment after any interior hole
    // (e.g. edit-after-cancel), landing later authoritative messages on the
    // wrong local slots. Place each message at its own authoritative slot.
    const threadIds: (string | undefined)[] = []
    for (const message of messages) {
      threadIds[message.index_in_thread] = message.id
    }
    this.currentThreadIds = threadIds
    this.invalidateThread(true)
    this.pingStructural()
  }

  getNextThreadIndex(): number {
    return this.currentThreadIds.length
  }

  getMessageAtThreadIndex(index: number): ChatMessage | undefined {
    const id = this.currentThreadIds[index]
    if (id === undefined) return undefined
    const entry = this.messagesById.get(id)
    return entry ? this.materializeSnapshot(entry) : undefined
  }

  placeLocalMessage(message: ChatMessage): void {
    this.insertMessage(message)
    this.padThreadTo(message.index_in_thread)
    this.currentThreadIds[message.index_in_thread] = message.id
    this.appendAllMessageId(message.id)
    this.invalidateThread(true)
    this.pingStructural()
  }

  applyNewMessage(event: MsgNew<ChatMessage>): void {
    // A message can land beyond the local thread end when the server holds
    // messages this client never received — e.g. the cancellation tail
    // re-broadcasts the finalized interrupted response and the synthetic
    // tool-return request at their persisted slots, which can sit past the
    // locally streamed end. Pad with holes so the message keeps its
    // authoritative index; holes drop at materialization.
    if (event.msg_index > this.currentThreadIds.length) {
      console.warn('[chat-stream-model] padding thread to message index', {
        messageId: event.message.id,
        msgIndex: event.msg_index,
        threadLength: this.currentThreadIds.length,
      })
      this.padThreadTo(event.msg_index)
    }

    const previousId = this.currentThreadIds[event.msg_index]
    const previousEntry =
      previousId === undefined ? undefined : this.messagesById.get(previousId)
    const previousWasOptimistic = Boolean(previousEntry?.snapshot.is_optimistic)
    // A promptless authoritative message (e.g. the cancellation tail's
    // synthetic tool-return) can land on the slot a just-typed optimistic
    // send took after local drift. It is not that send's confirmation —
    // consuming the send would blank the user's bubble until the real
    // confirm arrives — so re-place the send at the next free slot and let
    // its own confirm resolve it there. Messages that carry user-prompt
    // content keep the existing consume-on-slot behavior.
    const displacesPreviousOptimistic =
      previousWasOptimistic &&
      previousEntry !== undefined &&
      previousId !== event.message.id &&
      !hasUserPromptContent(event.message)

    this.insertMessage(event.message)
    this.currentThreadIds[event.msg_index] = event.message.id

    // Replace the confirmed optimistic user message in the all-messages
    // order; else upsert.
    if (
      previousWasOptimistic &&
      !displacesPreviousOptimistic &&
      previousId !== undefined &&
      previousId !== event.message.id &&
      !this.allMessageIdSet.has(event.message.id)
    ) {
      const optimisticIndex = this.allMessageIds.indexOf(previousId)
      if (optimisticIndex !== -1) {
        this.allMessageIds[optimisticIndex] = event.message.id
        this.allMessageIdSet.delete(previousId)
        this.allMessageIdSet.add(event.message.id)
      } else {
        this.appendAllMessageId(event.message.id)
      }
    } else {
      this.appendAllMessageId(event.message.id)
    }

    if (displacesPreviousOptimistic && previousEntry !== undefined) {
      const displacedSnapshot = this.materializeSnapshot(previousEntry)
      const nextIndex = this.currentThreadIds.length
      this.insertMessage({ ...displacedSnapshot, index_in_thread: nextIndex })
      this.currentThreadIds[nextIndex] = displacedSnapshot.id
    }

    // Drop optimistic duplicates of the authoritative message.
    const thread = this.materializeThread(this.currentThreadIds)
    const duplicateIds = new Set<string>(
      findDuplicateOptimisticUserMessageIds(thread, event.message),
    )
    duplicateIds.delete(event.message.id)
    // Index drift — e.g. a cancelled run's partial response still occupies
    // a local slot the server discarded — breaks exact-index matching.
    // When this confirm neither landed on an optimistic slot nor matched
    // by index, it still confirms the earliest pending send with the same
    // content; leaving that send in place renders a duplicate user bubble
    // until the next refresh.
    if (!previousWasOptimistic && duplicateIds.size === 0) {
      const confirmedId = findConfirmedOptimisticUserMessageId(
        thread,
        event.message,
      )
      if (confirmedId !== null) duplicateIds.add(confirmedId)
    }
    if (duplicateIds.size > 0) {
      // Interior holes, not compaction: local slots must keep tracking the
      // server's msg_index. Filtering shifted every later slot left, so the
      // run's subsequent messages landed beyond the shrunken thread end and
      // were dropped. Trailing holes are different — they are tail messages
      // the SERVER never had (e.g. a deduped optimistic past a discarded
      // partial), so nothing will ever fill them; trim them so the next
      // authoritative slot stays aligned.
      this.currentThreadIds = dropRightWhile(
        this.currentThreadIds.map((id) =>
          id !== undefined && duplicateIds.has(id) ? undefined : id,
        ),
        (id) => id === undefined,
      )
      this.allMessageIds = this.allMessageIds.filter(
        (id) => !duplicateIds.has(id),
      )
      for (const id of duplicateIds) this.allMessageIdSet.delete(id)
    }

    this.setSessionMeta({ currentRunId: event.message.run_id })
    this.invalidateThread(true)
    this.pingStructural()
  }

  /** True while a delta is held back waiting for an earlier sequence. */
  hasDeltaGap(): boolean {
    return this.deltaBuffers.size > 0
  }

  clearDeltaState(): void {
    this.nextExpectedSequence.clear()
    this.deltaBuffers.clear()
  }

  setSessionRunStatus(
    streamState: SessionMetaSnapshot['streamState'],
    currentRunId?: UUID | null,
  ): void {
    this.setSessionMeta(
      currentRunId === undefined
        ? { streamState }
        : { streamState, currentRunId },
    )
  }

  setFollowups(followups: string[] | undefined): void {
    this.setSessionMeta({ followups })
  }

  // ---- queued sends -----------------------------------------------------

  enqueueMessage(message: QueuedChatMessage): void {
    this.queuedMessages = [...this.queuedMessages, message]
    this.ping('queue')
  }

  dequeueMessages(queueIds?: readonly string[]): void {
    const next = queueIds
      ? this.queuedMessages.filter(
          (message) => !queueIds.includes(message.queueId),
        )
      : []
    if (next.length === this.queuedMessages.length) return
    this.queuedMessages = next
    this.ping('queue')
  }

  getQueuedMessages(): readonly QueuedChatMessage[] {
    return this.queuedMessages
  }

  // ---- the viewer's sends -----------------------------------------------

  noteSent(): void {
    this.sentCount += 1
    this.ping('sent')
  }

  getSentCount(): number {
    return this.sentCount
  }

  // ---- run lifecycle ----------------------------------------------------

  setRunStatus(runState: ChatRunState, activeRunId?: UUID | null): void {
    const nextActiveRunId =
      activeRunId === undefined ? this.run.activeRunId : activeRunId
    const next: ChatRunSnapshot = {
      ...this.run,
      runState,
      isStreaming: runState !== 'idle',
      activeRunId: runState === 'idle' ? null : nextActiveRunId,
    }
    if (shallow(next, this.run)) return
    this.run = next
    this.ping('runState')
  }

  setRunState(runState: ChatRunState): void {
    this.setRunStatus(runState)
  }

  setIsStreaming(isStreaming: boolean): void {
    this.setRunStatus(isStreaming ? 'streaming' : 'idle')
  }

  setActiveRunId(activeRunId: UUID | null): void {
    if (this.run.activeRunId === activeRunId) return
    this.run = { ...this.run, activeRunId }
    this.ping('runState')
  }

  setCompactionStatus(compactionStatus: ChatCompactionStatus): void {
    if (this.run.compactionStatus === compactionStatus) return
    this.run = { ...this.run, compactionStatus }
    this.ping('runState')
  }

  setSocketReadyState(socketReadyState: number): void {
    if (this.run.socketReadyState === socketReadyState) return
    this.run = { ...this.run, socketReadyState }
    this.ping('runState')
  }

  // ---- reads ------------------------------------------------------------

  getRunSnapshot(): ChatRunSnapshot {
    return this.run
  }

  getIsStreaming(): boolean {
    return this.run.isStreaming
  }

  getRunState(): ChatRunState {
    return this.run.runState
  }

  getSessionMeta(): SessionMetaSnapshot {
    return this.sessionMeta
  }

  getThreadShape(): ThreadShapeSnapshot {
    this.threadShapeCache ??= {
      structureVersion: this.structureVersion,
      contentVersion: this.contentVersion,
      currentThreadIds: this.currentThreadIds.filter((id) => id !== undefined),
    }
    return this.threadShapeCache
  }

  getMessage(messageId: string): ChatMessage | undefined {
    const entry = this.messagesById.get(messageId)
    return entry ? this.materializeSnapshot(entry) : undefined
  }

  getCurrentThread(): readonly ChatMessage[] {
    if (
      !this.currentThreadCache ||
      this.currentThreadCache.version !== this.contentVersion
    ) {
      this.currentThreadCache = {
        version: this.contentVersion,
        value: this.materializeThread(this.currentThreadIds),
      }
    }
    return this.currentThreadCache.value
  }

  getAllMessages(): readonly ChatMessage[] {
    if (
      !this.allMessagesCache ||
      this.allMessagesCache.version !== this.contentVersion
    ) {
      this.allMessagesCache = {
        version: this.contentVersion,
        value: this.materializeThread(this.allMessageIds),
      }
    }
    return this.allMessagesCache.value
  }

  getStreamedMessages(): ChatMessage[] {
    return [...this.nextExpectedSequence].flatMap(([id, next]) => {
      const message = this.getMessage(id)
      return message ? [{ ...message, last_delta_sequence: next - 1 }] : []
    })
  }

  getPartText(messageId: string, partIndex: number): string | undefined {
    const entry = this.messagesById.get(messageId)
    if (!entry) return undefined
    const parts = entry.draftParts ?? entry.snapshot.message.parts
    const part = parts[partIndex]
    if (!part) return undefined
    if ('content' in part && typeof part.content === 'string') {
      return part.content
    }
    // Tool-call parts stream their args as a growing string.
    if ('args' in part && typeof part.args === 'string') {
      return part.args
    }
    return undefined
  }

  getSubagentSnapshot(
    toolCallId: string,
  ): Readonly<SubagentChatSnapshot> | undefined {
    return this.subagentSnapshots?.[toolCallId]
  }

  getSubagentSnapshots(): SubagentSnapshotsByToolCallId | undefined {
    return this.subagentSnapshots
  }

  // ---- internals --------------------------------------------------------

  private invalidateThread(structural: boolean): void {
    if (structural) this.structureVersion += 1
    this.contentVersion += 1
    this.threadShapeCache = null
    this.currentThreadCache = null
    this.allMessagesCache = null
  }

  private insertMessage(message: ChatMessage): void {
    this.messagesById.set(message.id, {
      snapshot: message,
      snapshotVersion: 0,
      version: 0,
      draftParts: null,
      draftDeferredToolCalls: undefined,
      draftHasDeferredToolCalls: false,
    })
  }

  private appendAllMessageId(messageId: string): void {
    if (this.allMessageIdSet.has(messageId)) return
    this.allMessageIdSet.add(messageId)
    this.allMessageIds.push(messageId)
  }

  /** Extends the thread with explicit `undefined` holes up to `index`. */
  private padThreadTo(index: number): void {
    while (this.currentThreadIds.length < index) {
      this.currentThreadIds.push(undefined)
    }
  }

  /** Lazily copies the parts array once per message so token appends are O(1). */
  private draftPartsFor(
    entry: MessageEntry,
  ): (ModelRequestPart | ModelResponsePart)[] {
    if (entry.draftParts === null) {
      entry.draftParts = [...entry.snapshot.message.parts]
    }
    return entry.draftParts
  }

  private materializeSnapshot(entry: MessageEntry): ChatMessage {
    if (entry.snapshotVersion === entry.version) return entry.snapshot

    const base = entry.snapshot
    const parts = entry.draftParts ?? base.message.parts
    const next: ChatMessage = {
      ...base,
      ...(entry.draftHasDeferredToolCalls
        ? { deferred_tool_calls: entry.draftDeferredToolCalls }
        : {}),
      // The draft parts array is typed as the request|response part union;
      // spreading `base.message` erases which side of the discriminated union
      // `parts` must match, though the draft always originated from this
      // message's own kind. Structurally identical — inference can't unify.
      message: {
        ...base.message,
        parts: [...parts],
      } as ChatMessage['message'],
    }
    entry.snapshot = next
    entry.snapshotVersion = entry.version
    return next
  }

  private materializeThread(
    ids: readonly (string | undefined)[],
  ): ChatMessage[] {
    return ids.flatMap((id) => {
      const entry = id === undefined ? undefined : this.messagesById.get(id)
      return entry ? [this.materializeSnapshot(entry)] : []
    })
  }

  private setSessionMeta(patch: Partial<SessionMetaSnapshot>): void {
    const next = { ...this.sessionMeta, ...patch }
    if (shallow(next, this.sessionMeta)) return
    this.sessionMeta = next
    this.ping('session')
  }

  // ---- delta application ------------------------------------------------

  private applyDelta(event: MsgDelta): void {
    const delta = event.delta

    switch (delta.op) {
      case 'init_request':
      case 'init_response': {
        const shared = {
          id: event.message_id,
          parent_id: delta.parent_id,
          run_id: delta.run_id,
          run_parent_id: delta.run_parent_id,
          run_user_id: delta.run_user_id,
          index_in_thread: delta.index_in_thread,
        }
        const message: ChatMessage =
          delta.op === 'init_request'
            ? { ...shared, message: { kind: 'request', parts: [] } }
            : {
                ...shared,
                message: {
                  kind: 'response',
                  parts: [],
                  model_name: delta.model_name,
                  timestamp: delta.timestamp,
                },
              }

        this.insertMessage(message)
        this.currentThreadIds[delta.index_in_thread] = event.message_id
        this.appendAllMessageId(event.message_id)
        this.setSessionMeta({ currentRunId: message.run_id })
        this.invalidateThread(true)
        this.pingStructural()
        return
      }

      case 'set_part': {
        const entry = this.messagesById.get(event.message_id)
        if (!entry) {
          warnUnknownMessage(event)
          return
        }
        if (entry.snapshot.message.kind === 'request') {
          if (!isModelRequestPart(delta.part)) return
        } else {
          if (isClosedResponseMessage(entry.snapshot)) return
          if (!isModelResponsePart(delta.part)) return
        }

        this.draftPartsFor(entry)[delta.part_index] = delta.part
        entry.version += 1
        this.invalidateThread(true)
        this.ping(`part:${event.message_id}:${delta.part_index}`)
        this.pingStructural()
        return
      }

      case 'apply_part_delta': {
        const entry = this.messagesById.get(event.message_id)
        if (!entry) {
          warnUnknownMessage(event)
          return
        }
        if (entry.snapshot.message.kind !== 'response') return
        if (isClosedResponseMessage(entry.snapshot)) return

        const parts = this.draftPartsFor(entry)
        const existing = parts[delta.part_index]
        if (existing !== undefined && !isModelResponsePart(existing)) return
        const updated = applyResponsePartDelta(existing, delta.delta)
        if (!updated) return

        // Growth of an EXISTING part — text, thinking, or tool-call args —
        // is the high-volume content path: only its part topic (plus
        // thread:any) pings, so structural subscribers skip it entirely. The
        // running leaves overlay the live text/args themselves. Part CREATION
        // is structural.
        const isContentDelta = existing !== undefined
        parts[delta.part_index] = updated
        entry.version += 1
        this.invalidateThread(!isContentDelta)
        this.ping(`part:${event.message_id}:${delta.part_index}`)
        if (isContentDelta) {
          this.ping('thread:any')
        } else {
          this.pingStructural()
        }
        return
      }

      case 'set_deferred_tool_calls': {
        const entry = this.messagesById.get(event.message_id)
        if (!entry) {
          warnUnknownMessage(event)
          return
        }
        entry.draftDeferredToolCalls = delta.deferred_tool_calls
        entry.draftHasDeferredToolCalls = true
        entry.version += 1
        this.invalidateThread(true)
        this.pingStructural()
        return
      }
    }
  }
}

export type SubagentPingFlushScheduler = (flush: () => void) => void

export interface ChatStreamModelOptions {
  /**
   * Coalesces subagent-topic pings: deltas applied between flushes notify
   * once per flush instead of once per event. Adapters pass an
   * animation-frame scheduler so live streams render at most once per
   * frame per card; the default (no scheduler) pings synchronously.
   */
  coalesceSubagentPings?: SubagentPingFlushScheduler
}

/** Browser scheduler for `coalesceSubagentPings`: one flush per frame. */
export const animationFrameSubagentPingScheduler: SubagentPingFlushScheduler = (
  flush,
) => {
  requestAnimationFrame(flush)
}

export function createChatStreamModel(
  options?: ChatStreamModelOptions,
): ChatStreamModel {
  return new ChatStreamModelImpl(options?.coalesceSubagentPings)
}
